/* eslint-disable camelcase */
import React, { Component } from 'react';
import { AxiosResponse } from 'axios';

import { TFreelancerData } from '../../types';
import FreelancerDetailsTextField from './common/FreelancerDetailsTextField';

type TFreelancerDetailsHealthFields = {
    has_health_problems: boolean;
    health_problems_description: string;
    needs_adjustments: boolean;
    adjustments_description: string;
    consent_code: string;
};

type TFreelancerDetailsHealthProps = {
    editionEnabled: boolean;
    freelancerData: TFreelancerData;
    onSave: (fields: any) => Promise<AxiosResponse<any, any>>;
};

type TFreelancerDetailsHealthState = {
    isEdit: boolean;
    isLoading: boolean;
    errorMessages: any[] | null;
    fields: TFreelancerDetailsHealthFields;
};

class FreelancerDetailsHealth extends Component<
    TFreelancerDetailsHealthProps,
    TFreelancerDetailsHealthState
> {
    constructor(props: TFreelancerDetailsHealthProps) {
        super(props);

        this.state = {
            isEdit: false,
            isLoading: false,
            errorMessages: null,
            fields: {
                has_health_problems: null,
                health_problems_description: '',
                needs_adjustments: null,
                adjustments_description: '',
                consent_code: '',
            },
        };

        this.handleEditButtonClick = this.handleEditButtonClick.bind(this);
        this.handleCancelButtonClick = this.handleCancelButtonClick.bind(this);
        this.handleSaveButtonClick = this.handleSaveButtonClick.bind(this);
        this.handleCheckboxFieldChange = this.handleCheckboxFieldChange.bind(this);
        this.handleTextFieldChange = this.handleTextFieldChange.bind(this);
    }

    // eslint-disable-next-line react/no-deprecated
    componentWillUpdate(nextProps: any, nextState: { isEdit: boolean }) {
        if (nextState.isEdit !== this.state.isEdit) {
            this.initFields();
        }
    }

    initFields() {
        const { freelancerData } = this.props;
        this.setState({
            fields: {
                has_health_problems: freelancerData.has_health_problems,
                health_problems_description: freelancerData.health_problems_description || '',
                needs_adjustments: freelancerData.needs_adjustments,
                adjustments_description: freelancerData.adjustments_description || '',
                consent_code: freelancerData.consent_code || '',
            },
        });
    }

    handleTextFieldChange(
        event: React.ChangeEvent<HTMLInputElement>,
        fieldName: string,
    ) {
        const { value } = event.target;
        this.setState(prevState => ({
            fields: { ...prevState.fields, [fieldName]: value },
        }));
    }

    handleCheckboxFieldChange(
        event: React.ChangeEvent<HTMLInputElement>,
        fieldName: string,
    ) {
        const { checked } = event.target;
        this.setState(prevState => ({
            fields: { ...prevState.fields, [fieldName]: checked },
        }));
    }

    handleEditButtonClick() {
        this.setState({
            isEdit: true,
        });
    }

    handleCancelButtonClick() {
        this.setState({
            isEdit: false,
            errorMessages: null,
        });
    }

    handleSaveButtonClick() {
        const { fields } = this.state;
        this.setState({
            isLoading: true,
            errorMessages: null,
        });
        this.props
            .onSave(fields)
            .then(() => {
                this.setState({
                    isEdit: false,
                    isLoading: false,
                });
            })
            .catch(
                (error: { response: { data: { meta: { detail: any[] } } } }) => {
                    const { detail } = error.response.data.meta;
                    const message = Object.values(detail)[0];

                    this.setState({
                        errorMessages: message,
                        isLoading: false,
                    });
                },
            );
    }

    render() {
        // eslint-disable-next-line react/prop-types
        const { freelancerData, editionEnabled } = this.props;
        const { isEdit, isLoading, errorMessages, fields } = this.state;

        return (
            <div className="freelancer-details-section">
                <div className="freelancer-details-section__header">
                    <div className="freelancer-details-section__title">
                        Health and Criminal Convictions
                    </div>
                    {editionEnabled &&
                        (isEdit ? (
                            <div className="freelancer-details-section__btns">
                                <button
                                    type="button"
                                    data-testid="saveBtn"
                                    disabled={isLoading}
                                    className="freelancer-details-section__btn"
                                    onClick={this.handleSaveButtonClick}
                                >
                                    Save
                                </button>
                                <button
                                    data-testid="cancelBtn"
                                    type="button"
                                    disabled={isLoading}
                                    className="freelancer-details-section__btn"
                                    onClick={this.handleCancelButtonClick}
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <div className="freelancer-details-section__btns">
                                <button
                                    data-testid="editBtn"
                                    type="button"
                                    className="freelancer-details-section__btn"
                                    onClick={this.handleEditButtonClick}
                                >
                                    <i className="icon icon-pencil-3" />
                                    Edit
                                </button>
                            </div>
                        ))}
                </div>
                <div>
                    {errorMessages && (
                        <div className="freelancer-details-section__errors">
                            {this.state.errorMessages.map((item: any) => (
                                <p data-testid="errorMsg" key={item.id}>
                                    {item}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
                <div className="freelancer-details-section__content">
                    <table className="table-default">
                        <tbody>
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    Worker has declared health issues or disability
                                </td>
                                {isEdit ? (
                                    <td>
                                        <input
                                            data-testid="healthProblemsInput"
                                            className="checkbox-purple"
                                            type="checkbox"
                                            checked={fields.has_health_problems}
                                            onChange={event => {
                                                this.handleCheckboxFieldChange(
                                                    event,
                                                    'has_health_problems'
                                                );
                                            }}
                                        />
                                    </td>
                                ) : (
                                    <td>
                                        <i className={
                                            // eslint-disable-next-line no-nested-ternary
                                            freelancerData.has_health_problems
                                                ? 'icon icon-check-12'
                                                : freelancerData.has_health_problems === false
                                                    ? 'icon icon-close2'
                                                    : ''
                                        }/>
                                    </td>
                                )}
                            </tr>
                            <FreelancerDetailsTextField
                                handleTextFieldChange={this.handleTextFieldChange}
                                isEdit={isEdit}
                                value={fields.health_problems_description}
                                apiField={freelancerData.health_problems_description}
                                fieldName="health_problems_description"
                                humanReadable="Description of health problems"
                                testId="healthProblemsDescInput"
                            />
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    Worker has asked that we consider reasonable adjustments
                                </td>
                                {isEdit ? (
                                    <td>
                                        <input
                                            data-testid="adjustmentsInput"
                                            className="checkbox-purple"
                                            type="checkbox"
                                            checked={fields.needs_adjustments}
                                            onChange={event => {
                                                this.handleCheckboxFieldChange(
                                                    event,
                                                    'needs_adjustments'
                                                );
                                            }}
                                        />
                                    </td>
                                ) : (
                                    <td>
                                        <i className={
                                            // eslint-disable-next-line no-nested-ternary
                                            freelancerData.needs_adjustments
                                                ? 'icon icon-check-12'
                                                : freelancerData.needs_adjustments === false
                                                    ? 'icon icon-close2'
                                                    : ''
                                        }/>
                                    </td>
                                )}
                            </tr>
                            <FreelancerDetailsTextField
                                handleTextFieldChange={this.handleTextFieldChange}
                                isEdit={isEdit}
                                value={fields.adjustments_description}
                                apiField={freelancerData.adjustments_description}
                                fieldName="adjustments_description"
                                humanReadable="Description of adjustments"
                                testId="adjustmentsDescInput"
                            />
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    Worker has unspent criminal convictions
                                </td>
                                <td>
                                    <i className={
                                        // eslint-disable-next-line no-nested-ternary
                                        freelancerData.has_unspent_convictions
                                            ? 'icon icon-check-12'
                                            : freelancerData.has_unspent_convictions === false
                                                ? 'icon icon-close2'
                                                : ''
                                    }/>
                                </td>
                            </tr>
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    Details of unspent criminal convictions
                                </td>
                                <td>
                                    {freelancerData.unspent_convictions_description}
                                </td>
                            </tr>
                            <FreelancerDetailsTextField
                                handleTextFieldChange={this.handleTextFieldChange}
                                isEdit={isEdit}
                                value={fields.consent_code}
                                apiField={freelancerData.consent_code}
                                fieldName="consent_code"
                                humanReadable="Consent code"
                                testId="consentCodeInput"
                            />
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

export default FreelancerDetailsHealth;
